import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  compact,
  get,
  isEmpty,
  noop,
  omitBy,
  uuOmit,
} from 'lodash';
import { validateReduxForm } from 'common/form';
import AddressField from 'common/form/AddressField/AddressField';
import CurrencyField from 'common/form/CurrencyField';
import { SHOW_STREET_CITY_STATE_POSTAL_CODE } from 'common/form/AddressField/constants';
import useZcodes from 'common/hooks/useZcodes';
import WarningMessage from 'common/utils/WarningMessage';
import {
  BaseCard,
  BaseCardBody,
  BaseCardHeader,
  DateField,
  decodeHTML,
  InputField,
  SelectField,
  TextField,
} from '@unite-us/ui';
import { NoteDisclosure, PaymentsSupportingDocuments, validations, DateTime as DateTimeField } from '@unite-us/app-components';
import { validateUnitAmount } from 'src/common/form/Interactions/utils';
import moment from 'moment';
import { Spinner } from 'common/spinners';
import InvoiceStatus from 'src/components/Cases/components/Detail/providedServices/InvoiceStatus';
import { useCurrentProviderId } from 'src/common/contexts/CurrentProviderContext';
import FormInteractionsButtons from 'common/form/Interactions/components/FormInteractionsButtons';
import InteractionNoteField from 'common/form/Interactions/components/InteractionNoteField';
import { findInsuranceByDateRange, formatTimeForApi, requestDateOutOfRange } from 'common/utils/Dates/utils';
import { useFindMostRecentInvoice, useCreateProvidedService, useUpdateProvidedService } from 'components/Cases/hooks';
import providedServiceInitialValues from '../../../../utils/providedServiceInitialValues';
import Details from './Details';
import FundsDistributedDetails from './FundsDistributedDetails';
import TimeBasedDetails from './TimeBasedDetails';
import ProcedureCodeRows from './ProcedureCodeRows';
import ServiceAuthorizationDetails from './ServiceAuthorizationDetails';
import './ProvidedServiceForm.scss';

const METAFIELD_COMPONENTS = {
  address: AddressField,
  date: DateField,
  datetime: DateTimeField,
  dropdown: SelectField,
  multipledropdown: SelectField,
  money: CurrencyField,
  number: InputField,
  text: InputField,
  textarea: TextField,
  uuid: InputField,
};

const DATE_SELECTOR_OPTIONS = [
  { label: 'Single Date', value: 'Single Date' },
  { label: 'Date Range', value: 'Date Range' },
];

const ZCODE_WARNING =
  'Note: Some Zcodes may be automatically selected and updated based on the contracted program.';

const validateField = (
  required,
  value = {},
  type,
  metafieldValidations = {},
) => {
  if (type === 'uuid' && !isEmpty(value.value)) {
    return validations.isValidUUID;
  }
const greaterThanValue = get(metafieldValidations, 'greater_than', null);

  return (fieldValue) => {
    const requiredError = validations.isRequired(fieldValue);
    if (required && requiredError) {
      return requiredError;
    }

    // Optional and empty: don't run any other validations.
    if (!required && requiredError) {
      return null;
    }

    if (
      greaterThanValue !== null &&
      Number(fieldValue) <= greaterThanValue
    ) {
      return `Value must be greater than ${greaterThanValue}`;
    }
    if (type === 'date' || type === 'datetime') {
      const selectedDate = moment.unix(fieldValue);
      if (!selectedDate.isValid()) {
        return 'Please enter a valid date';
      }

      const minDate = moment.utc().subtract(150, 'years').startOf('day');
      const maxDate = moment.utc().add(1, 'years').endOf('day');

      if (selectedDate.isBefore(minDate)) {
        return `Date must be on or after ${minDate.format('MM/DD/YYYY')}`;
      }

      if (selectedDate.isAfter(maxDate)) {
        return `Date must be on or before ${maxDate.format('MM/DD/YYYY')}`;
      }
    }
    return null;
  };
};

const HelpText = ({ text }) => (
  /* eslint-disable react/no-danger */
  <div
    className="payments-track-service__help-text"
    dangerouslySetInnerHTML={{ __html: text }}
  />
  /* eslint-enable react/no-danger */
);
HelpText.propTypes = {
  text: PropTypes.string.isRequired,
};

const SectionSeparator = ({ className }) => (
  <div className={cx(className, 'border-t border-solid border-dark-fill-blue -mx-2 lg:-mx-8 xl:-mx-16')} />
);
SectionSeparator.propTypes = {
  className: PropTypes.string,
};
SectionSeparator.defaultProps = {
  className: '',
};

export function ProvidedServiceForm(props) {
  const {
    cancelFunction,
    casePayerId,
    feeScheduleProgram,
    fields,
    fileUploads,
    formName,
    handleSubmit,
    initializeForm,
    isBillable,
    isEditing,
    metafields,
    paymentType,
    planId,
    providedService,
    registerField,
    resetForm,
    serviceAuthorization,
    serviceAuthorizationInvoiceInformation,
    serviceCase,
    setInferredPlanId,
    socialInsurances,
    submitting,
  } = props;
  const [enforceRequiredFields, setEnforceRequiredFields] = useState(true);
  const [insuranceExpired, setInsuranceExpired] = useState(false);

  useEffect(() => {
    const capOrUnitInitialValue = paymentType === 'cost_based_reimbursement' ?
      feeScheduleProgram?.cap_information :
      feeScheduleProgram?.unit;

    initializeForm(providedServiceInitialValues({
      feeScheduleProgram,
      providedService,
      capOrUnitInitialValue,
      metafields,
      fileUploads,
      isBillable,
    }));
  }, [formName]);

  const providerId = useCurrentProviderId();
  const serviceCaseId = serviceCase?.id;
  const programId = serviceCase?.program?.id;
  const { data: zcodes } = useZcodes(feeScheduleProgram?.id, casePayerId, feeScheduleProgram?.force_zcode_associations);
  const feeSchedule = feeScheduleProgram?.fee_schedule;
  const ignoreSocialCareExpiredDates = !!feeSchedule?.ignore_social_care_expired_dates;

  const { createProvidedService, isReady: isCreateReady } = useCreateProvidedService(feeScheduleProgram.id);
  const { updateProvidedService, isReady: isUpdateReady } = useUpdateProvidedService(providedService.id);
  const isReady = isCreateReady && (!providedService.id || isUpdateReady);

  const findInsurance = useCallback((insurances, start_date, end_date) => {
    const insurance = findInsuranceByDateRange(
      {
        insurances,
        startDate: start_date,
        endDate: end_date,
        ignoreSocialCareExpiredDates,
        serviceAuthorization,
        setInsuranceExpired: (value) => setInsuranceExpired(value),
      },
    );
    setInferredPlanId(insurance?.plan?.id);
    return insurance;
  }, [findInsuranceByDateRange, serviceAuthorization, setInferredPlanId, setInsuranceExpired]);

  const unitAmountValidations = useCallback(
    (value) => validateUnitAmount(value, enforceRequiredFields),
    [enforceRequiredFields],
  );

  const validateInsuranceDates = useCallback(
    () => {
      const isSingleDateRequested = fields?.provided_service?.period_of_service?.value === 'Single Date';
      let start_date = fields?.provided_service?.service_start_date?.value;
      let end_date = isSingleDateRequested ? null : fields?.provided_service?.service_end_date?.value;

      if (start_date) {
        start_date = moment.unix(start_date);
        if (!end_date) {
          return !!findInsurance(socialInsurances, start_date, start_date, ignoreSocialCareExpiredDates);
        }
        end_date = moment.unix(end_date);
        return !!findInsurance(socialInsurances, start_date, end_date, ignoreSocialCareExpiredDates);
      }
      setInferredPlanId(null);
      return false;
    },
    [
      socialInsurances,
      fields?.provided_service?.period_of_service?.value,
      fields?.provided_service?.service_start_date?.value,
      fields?.provided_service?.service_end_date?.value,
      findInsurance,
      ignoreSocialCareExpiredDates,
      setInferredPlanId,
    ],
  );

  const transformMetadataFormValues = useCallback(({
    address, field, value,
  }) => {
    const metafield = metafields.find((mf) => mf.field === field);
    if (!metafield) {
      return null;
    }

    const compactAddress = omitBy(address, (addressValue) => isEmpty(addressValue));

    switch (metafield.type) {
      case 'address':
        return {
          field,
          value: !isEmpty(compactAddress) ? compactAddress : null,
        };
      case 'datetime':
      case 'date':
        return {
          field,
          value: value ? new Date(value * 1000).toISOString() : value,
        };
      case 'dropdown':
        return {
          field,
          value: value ? decodeHTML(value) : value,
        };
      default:
        return { field, value };
    }
  }, [metafields]);

  const formatProvidedService = useCallback((providedServiceFormValues, providedServiceState) => {
    const {
      distribution_reason,
      metadata,
      payment_method,
      period_of_service,
      procedure_code_rows,
      start_time_hour,
      start_time_minute,
      start_time_ampm,
    } = providedServiceFormValues;

    const serviceDurationStartTime = formatTimeForApi(
      start_time_hour,
      start_time_minute,
      start_time_ampm,
    );

    let procedure_codes = [];
    let procedure_code_modifiers = [];
    if (procedure_code_rows) {
      procedure_codes = procedure_code_rows.map((row) => row?.procedure_code);
      procedure_code_modifiers = procedure_code_rows.map((row) => row?.procedure_code_modifiers).flat();
    }

    // ** NOTE ** If form value comes from a SelectField, make sure to decodeHTML

    const transformedMetaData = !isEmpty(metadata) ? metadata.map(transformMetadataFormValues) : [];
    const decodedDistributionReason = distribution_reason ? decodeHTML(distribution_reason) : null;
    const decodedPaymentMethod = payment_method ? decodeHTML(payment_method) : null;

    const providedServiceValues = uuOmit(
      providedServiceFormValues,
      [
        'form_id',
        'period_of_service',
        'procedure_code_rows',
        'start_time_ampm',
        'start_time_hour',
        'start_time_minute',
        'unit',
      ],
    );

    const convertToISOString = (timestamp) => (timestamp ? new Date(timestamp * 1000).toISOString() : undefined);
    const starts_at_val = convertToISOString(providedServiceValues.service_start_date);
    const isSingleDate = period_of_service === 'Single Date';
    const ends_at_val = !isSingleDate ? convertToISOString(providedServiceValues.service_end_date) : null;

    return {
      ...providedServiceValues,
      distribution_reason: decodedDistributionReason,
      metadata: compact(transformedMetaData),
      payment_method: decodedPaymentMethod,
      state: providedServiceState,
      procedure_codes: compact(procedure_codes),
      procedure_code_modifiers: compact(procedure_code_modifiers),
      service_duration_start_time: serviceDurationStartTime,
      starts_at: starts_at_val,
      service_start_date: undefined,
      ends_at: ends_at_val,
      service_end_date: undefined,
      notes: providedServiceValues.note,
      note: undefined,
      case: serviceCaseId,
      program: programId,
      plan: planId || undefined,
    };
  }, [transformMetadataFormValues, serviceCaseId, providerId, programId, planId]);

  const renderMetafields = useCallback(() => (
    metafields.map(({
      field: fieldKey,
      help_text,
      label,
      options,
      type,
      validations: metafieldValidations,
    }) => {
      const dropdownOptions = (options || []).map((option) => ({
        field: option,
        value: option,
      }));
      const field = fields.provided_service.metadata.find((md) => md.field.value === fieldKey);
      if (!field) {
        return (
          <Spinner key={fieldKey} />
        );
      }
      /*
        The metafield is required
         * if the validations required key is true AND
         * if enforceRequiredFields === true
            - the fee schedule program is non-billable or the user submits the contracted service
      */
      const required = get(metafieldValidations, 'required', false) && enforceRequiredFields;
      const hint = isEmpty(help_text) ? '' : <HelpText text={help_text} />;
      const MetafieldComponent = METAFIELD_COMPONENTS[type];
      switch (type) {
        case 'file':
          return null;
        case 'address':
          return (
            <>
              <label htmlFor={`${fieldKey}-address-field`} className="ui-form-field__label">{label}</label>
              <MetafieldComponent
                addressPath="provided_service.metadata[].address"
                displayFieldSet={SHOW_STREET_CITY_STATE_POSTAL_CODE}
                field={field.address}
                forceRequired={required}
                hideMailingField
                id={`${fieldKey}-address-field`}
                inline={false}
                key={fieldKey}
                labelStyle={{
                  color: 'var(--color-text-blue)',
                  fontFamily: 'Proxima Nova Medium',
                  fontSize: '13px',
                  textTransform: 'none',
                }}
                requireAddressLine1={required}
                registerField={registerField}
              />
            </>
          );
        default:
          return (
            <MetafieldComponent
              field={field?.value}
              hint={hint}
              inline={false}
              key={fieldKey}
              id={fieldKey}
              label={label}
              labelKey="field"
              multiple={type === 'multipledropdown'}
              options={dropdownOptions}
              required={required}
              ref={registerField}
              type={type}
              validations={validateField(required, field?.value, type, metafieldValidations)}
              valueKey="value"
            />
          );
      }
    })
  ), [metafields, fields.provided_service.metadata, enforceRequiredFields, registerField]);

  const onDraftOrSubmit = useCallback(
    async (formValues, providedServiceState = 'active') => {
      const provServFormatted = formatProvidedService(formValues.provided_service, providedServiceState);

      let response;
      if (isEditing) {
        response = await updateProvidedService(provServFormatted);
      } else {
        response = await createProvidedService(provServFormatted);
      }

      resetForm();
      cancelFunction();
      return response?.data?.data?.id;
    },
    [
      formatProvidedService,
      isEditing,
      createProvidedService,
      updateProvidedService,
    ],
  );

  const onSaveDraftBillable = useCallback((event) => {
    /*
      Don't enforce validations when the user submits the contracted service as a draft
      for billable fee schedule programs.
    */
    setEnforceRequiredFields(false);
    handleSubmit((formValues) => onDraftOrSubmit(formValues, 'active'))(event);
  }, [handleSubmit, onDraftOrSubmit]);

  const isTimeBased = paymentType === 'time_based';
  const isFundsDistributed = paymentType === 'funds_distributed';
  const usesDetailsForm = !!paymentType && !isFundsDistributed && !isTimeBased;

  const requiredMetaFieldFiles = metafields.filter(({ type }) => type === 'file');

  // Memoize the insurance date validation to prevent infinite loops
  const insuranceDateNonValid = useMemo(() => !validateInsuranceDates(), [
    validateInsuranceDates,
    fields?.provided_service?.period_of_service?.value,
    fields?.provided_service?.service_start_date?.value,
    fields?.provided_service?.service_end_date?.value,
  ]);

  const negativeAuthorizedAmount = serviceAuthorizationInvoiceInformation?.remainingAuthorizedAmount < 0;
  const unitAmountExceedsAuthorizedAmount =
    fields?.provided_service?.unit_amount?.value > serviceAuthorizationInvoiceInformation?.remainingAuthorizedAmount;
  const requestedValueNonValid = negativeAuthorizedAmount || (!isTimeBased && unitAmountExceedsAuthorizedAmount);

  const isSingleDateRequested = fields?.provided_service?.period_of_service?.value === 'Single Date';
  const requestedStartDate = fields?.provided_service?.service_start_date?.value;
  const requestedEndDate = isSingleDateRequested ? null : fields?.provided_service?.service_end_date?.value;
  const serviceAuthorizationStartDate = serviceAuthorizationInvoiceInformation?.approvedStartsAt;
  const serviceAuthorizationEndDate = serviceAuthorizationInvoiceInformation?.approvedEndsAt;
  const requestedDatesNonValid = (
    requestDateOutOfRange(
      requestedStartDate && moment.unix(requestedStartDate),
      serviceAuthorizationStartDate,
      serviceAuthorizationEndDate,
    ) ||
    requestDateOutOfRange(
      requestedEndDate && moment.unix(requestedEndDate),
      serviceAuthorizationStartDate,
      serviceAuthorizationEndDate,
    )
  );

  const isAuthorizationSpendLoaded = !!serviceAuthorizationInvoiceInformation?.isSuccess;

  const idsMatch = (objects1, objects2) => {
    const ids1 = objects1?.map((obj) => (obj.id))?.sort();
    const ids2 = objects2?.map((obj) => (obj.id))?.sort();
    return (ids1?.length === ids2?.length) && ids1?.every((id1, i) => (id1 === ids2[i]));
  };

  const feeScheduleProgramName = feeScheduleProgram?.name;
  const canInvoiceAboveRemainingAuthorizedAmount = !!feeScheduleProgram?.can_invoice_above_remaining_authorized_amount;
  const placeOfServiceOptions = feeScheduleProgram?.place_of_services
    ?.map((pos) => ({ label: `${pos.code} - ${pos.description}`, value: pos.id })) ?? [];
  const zcodeOptions = feeScheduleProgram?.force_zcode_associations ? [] :
    zcodes.map((zcode) => ({ label: `${zcode.code} - ${zcode.description}`, value: zcode.id }));
  const distributionReasons = feeScheduleProgram?.distribution_reasons || [];
  const distributionReasonOptions = distributionReasons.map((option) => ({
    field: option,
    value: option,
  }));
  const paymentMethods = feeScheduleProgram?.payment_methods || [];
  const paymentMethodOptions = paymentMethods.map((option) => ({
    field: option,
    value: option,
  }));

  const forceZcodes = feeScheduleProgram.force_zcode_associations;
  const showZcodeSelect = !forceZcodes && zcodeOptions.length > 0;
  const showPlaceOfServiceSelect = placeOfServiceOptions.length > 0;
  const fspHasProcedureCodes = !isEmpty(feeScheduleProgram.procedure_codes);
  const psHasProcedureCodes = !isEmpty(providedService.procedure_codes);
  const showProcedureCodeSelects = fspHasProcedureCodes || psHasProcedureCodes;
  const zcodeIdsMatch = idsMatch(providedService?.zcodes, feeScheduleProgram?.zcodes);
  const showZcodeWarning = isEditing && forceZcodes && !zcodeIdsMatch;

  const hasAdditionalInfo =
    showZcodeWarning ||
    showZcodeSelect ||
    showPlaceOfServiceSelect ||
    showProcedureCodeSelects ||
    !isEmpty(metafields);

  const { invoice } = useFindMostRecentInvoice(providedService.id);

  return (
    <BaseCard className={isEditing ? 'payments-track-service-card--edit' : 'payments-track-service-card--new'}>
      <BaseCardHeader title={feeScheduleProgramName} noBorder={!!invoice} />
      {!!invoice && (
        <div className="bg-white flex gap-1 pl-1 border-b border-solid border-dark-fill-blue">
          <InvoiceStatus invoice={invoice} />
        </div>
      )}
      <BaseCardBody className="pt-8 pb-9">
        <form className="payments-track-service px-4 lg:px-16 xl:px-32" data-role="provided-service-form">
          {serviceAuthorization && (
            <>
              <h3 className="text-lg text-text-blue mb-4 leading-7">Authorization Details</h3>
              <ServiceAuthorizationDetails
                {...serviceAuthorizationInvoiceInformation}
                isTimeBased={isTimeBased}
                unitAmountCalculationHint={feeScheduleProgram.unit_amount_calculation_hint}
              />
              <SectionSeparator className="my-10" />
            </>
          )}
          <h3 className="text-lg text-text-blue mb-4 leading-7">Service Details</h3>
          {isFundsDistributed && (
            <FundsDistributedDetails
              fields={fields}
              registerField={registerField}
              distributionReasonOptions={distributionReasonOptions}
              paymentMethodOptions={paymentMethodOptions}
            />
          )}
          {isTimeBased && (
            <TimeBasedDetails
              fields={fields}
              registerField={registerField}
              enforceRequiredFields={enforceRequiredFields}
              unitAmountCalculationHint={feeScheduleProgram.unit_amount_calculation_hint}
              remainingAuthorizedAmount={serviceAuthorizationInvoiceInformation?.remainingAuthorizedAmount}
              canInvoiceAboveRemainingAuthorizedAmount={canInvoiceAboveRemainingAuthorizedAmount}
              feeScheduleProgramName={feeScheduleProgramName}
              ignoreSocialCareExpiredDates={ignoreSocialCareExpiredDates}
              insuranceDateNonValid={insuranceDateNonValid}
              insuranceExpired={insuranceExpired}
              requestedDatesNonValid={requestedDatesNonValid}
            />
          )}
          {usesDetailsForm && (
            <Details
              enforceRequiredFields={enforceRequiredFields}
              feeScheduleProgramName={feeScheduleProgramName}
              fields={fields}
              canInvoiceAboveRemainingAuthorizedAmount={canInvoiceAboveRemainingAuthorizedAmount}
              ignoreSocialCareExpiredDates={ignoreSocialCareExpiredDates}
              insuranceDateNonValid={insuranceDateNonValid}
              insuranceExpired={insuranceExpired}
              requestedDatesNonValid={requestedDatesNonValid}
              isBillable={isBillable}
              paymentType={paymentType}
              registerField={registerField}
              unitAmountValidations={unitAmountValidations}
              options={DATE_SELECTOR_OPTIONS}
              requestedValueNonValid={requestedValueNonValid}
            />
          )}
          <div id="additional-information" className={cx((!hasAdditionalInfo) && 'hidden')}>
            <SectionSeparator className="mt-1 mb-10" />
            <h3 className="text-lg text-text-blue normal-case tracking-normal leading-7 mb-4">
              Additional Information
            </h3>
            {showZcodeWarning && (
              <div className="flex mb-10">
                <WarningMessage>{ZCODE_WARNING}</WarningMessage>
              </div>
            )}
            {showZcodeSelect && (
              <SelectField
                id="provided-service-zcodes"
                field={fields.provided_service.zcodes}
                label="Diagnosis Code (Z-Code)"
                multiple
                options={zcodeOptions}
                placeholder="Z-codes"
                autoSelectValue
                className={zcodeOptions.length <= 1 ? 'hidden' : 'mt-5 mb-10'}
                ref={registerField}
                validations={(val) => validations.isRequired(val)}
                required
              />
            )}
            {showPlaceOfServiceSelect && (
              <SelectField
                id="provided-service-place_of_service"
                field={fields.provided_service.place_of_service}
                label="Place of Service"
                options={placeOfServiceOptions}
                placeholder="Place of Service"
                className="mt-5 mb-10"
                required
                ref={registerField}
                validations={(val) => validations.isRequired(val)}
              />
            )}
            {showProcedureCodeSelects && (
              <ProcedureCodeRows
                casePayerId={casePayerId}
                feeScheduleProgram={feeScheduleProgram}
                fields={fields}
                isEditing={isEditing}
                providedService={providedService}
                registerField={registerField}
              />
            )}

            {!isEmpty(metafields) && renderMetafields()}
          </div>
          <SectionSeparator className="mt-5 mb-10" />
          <div className="payments-track-service__notes">
            <InteractionNoteField
              afterLabelContent={<NoteDisclosure />}
              field={fields.provided_service.note}
              label={(
                <h3 className="text-lg text-text-blue normal-case tracking-normal leading-7 text-left">
                  Note
                </h3>
              )}
              registerField={registerField}
              required={false}
            />
          </div>
          <div className={cx(submitting && 'relative z-0')}>
            {!isEmpty(requiredMetaFieldFiles) && (
              <>
                <SectionSeparator className="mt-4 mb-10" />
                <h3 className="text-lg text-text-blue mb-4 leading-7">
                  Supporting Documents
                </h3>
                <p className="mb-10 text-13px">
                  Upload your documents from your device. Acceptable formats are .CSV, .DOCX, .PDF, .JPG, or .PNG
                </p>
                <div className="space-y-8">
                  {
                    requiredMetaFieldFiles.map(({ field: fieldName, label, validations: metafieldValidations }) => {
                      const field = fields.provided_service.metadata.find((metadata) => (
                        metadata.field.value === fieldName
                      ));
                      const requiredMetaField = get(metafieldValidations, 'required', false);

                      return field ? (
                        <PaymentsSupportingDocuments
                          key={fieldName}
                          label={label}
                          fieldName={fieldName}
                          field={field.value}
                          required={requiredMetaField && enforceRequiredFields}
                          ref={registerField}
                          validations={requiredMetaField && enforceRequiredFields ? validations.isRequired : noop}
                        />
                      ) : null;
                    })
                  }
                </div>
              </>
            )}
            {submitting && !isEmpty(requiredMetaFieldFiles) && (
              <div className="absolute inset-0 flex justify-center items-center z-10">
                <Spinner />
              </div>
            )}
          </div>

          <SectionSeparator className="p-0 mb-6 -mx-4 lg:-mx-16 xl:-mx-32" />
          {isBillable ? (
            <FormInteractionsButtons
              secondaryActionHandler={onSaveDraftBillable}
              primaryActionHandler={handleSubmit((formValues) => onDraftOrSubmit(formValues, 'submitted'))}
              cancelActionHandler={cancelFunction}
              submitting={submitting}
              interaction="fee-schedule-provided-service"
              primaryLabel={'SUBMIT FOR REVIEW'}
              secondaryLabel={'SAVE AS DRAFT'}
              conditionToDisableButton={
                !isReady ||
                !isAuthorizationSpendLoaded ||
                insuranceDateNonValid || (
                  !canInvoiceAboveRemainingAuthorizedAmount && (
                    requestedValueNonValid ||
                    requestedDatesNonValid
                  )
                )
              }
              conditionToDisableSecondaryButton={
                !isReady ||
                !isAuthorizationSpendLoaded ||
                insuranceDateNonValid || (
                  !canInvoiceAboveRemainingAuthorizedAmount && (
                    requestedValueNonValid ||
                    requestedDatesNonValid
                  )
                )
              }
            />
          ) : (
            <FormInteractionsButtons
              primaryActionHandler={handleSubmit((formValues) => onDraftOrSubmit(formValues, 'active'))}
              cancelActionHandler={cancelFunction}
              submitting={submitting}
              interaction="fee-schedule-provided-service"
              primaryLabel={'SAVE'}
              conditionToDisableButton={insuranceDateNonValid || requestedValueNonValid || requestedDatesNonValid}
            />
          )}
        </form>
      </BaseCardBody>
    </BaseCard>
  );
}

ProvidedServiceForm.propTypes = {
  cancelFunction: PropTypes.func,
  fields: PropTypes.object.isRequired,
  fileUploads: PropTypes.array.isRequired,
  formName: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initializeForm: PropTypes.func.isRequired,
  isBillable: PropTypes.bool.isRequired,
  isEditing: PropTypes.bool,
  metafields: PropTypes.array.isRequired,
  paymentType: PropTypes.string,
  registerField: PropTypes.func.isRequired,
  resetForm: PropTypes.func.isRequired,
  serviceCase: PropTypes.object,
  submitting: PropTypes.bool.isRequired,
  serviceAuthorization: PropTypes.object,
  socialInsurances: PropTypes.array.isRequired,
  serviceAuthorizationInvoiceInformation: PropTypes.object,
  planId: PropTypes.string,
  setInferredPlanId: PropTypes.func.isRequired,
  casePayerId: PropTypes.string,
  feeScheduleProgram: PropTypes.object,
  providedService: PropTypes.object.isRequired,
};

ProvidedServiceForm.defaultProps = {
  cancelFunction: noop,
  isEditing: false,
  paymentType: '',
  serviceAuthorization: null,
  serviceAuthorizationInvoiceInformation: null,
  planId: null,
  feeScheduleProgram: {},
  casePayerId: undefined,
  serviceCase: {},
};

const fields = [
  'provided_service.form_id',
  'provided_service.id',
  'provided_service.unit_amount',
  'provided_service.actual_unit_amount',
  'provided_service.unit',
  'provided_service.payee',
  'provided_service.distribution_reason',
  'provided_service.payment_method',
  'provided_service.distribution_id',
  'provided_service.period_of_service',
  'provided_service.service_start_date',
  'provided_service.service_end_date',
  'provided_service.service_duration',
  'provided_service.start_time_hour',
  'provided_service.start_time_minute',
  'provided_service.start_time_ampm',
  'provided_service.note',
  'provided_service.metadata[].value',
  'provided_service.metadata[].field',
  'provided_service.metadata[].address.city',
  'provided_service.metadata[].address.line_1',
  'provided_service.metadata[].address.line_2',
  'provided_service.metadata[].address.postal_code',
  'provided_service.metadata[].address.state',
  'provided_service.zcodes',
  'provided_service.place_of_service',
  'provided_service.procedure_code_rows[]',
  'provided_service.procedure_code_rows[].procedure_code',
  'provided_service.procedure_code_rows[].procedure_code_modifiers',
];

export default validateReduxForm({
  fields,
})(ProvidedServiceForm);
